// 9fbef606107a605d69c0edbcd8029e5d
import isEmpty from 'lodash/isEmpty';
import { parseBoolean } from '@tcp/core/src/utils/utils';
import {
  renderLabel,
  getLabelFromCopyOrFromTranslation,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { routerPush, getAPIConfig, getViewportInfo } from '@tcp/core/src/utils';
import pushEventToAdobeAnalytics, {
  getPLPPageName,
} from '@tcp/core/src/adobeAnalytics/adobe.utils';

export const getTagName = (isCompleteTagName, tagName) =>
  parseBoolean(isCompleteTagName) ? tagName : `plp_${tagName}_us_gallery`;

export const generateUniqueKey = (id, slot, tagName) => {
  const sanitizedId = id?.toString().replace(/_/g, '-');
  const sanitizedSlot = slot.replace(/_/g, '-');
  const sanitizedTagName = tagName.replace(/_/g, '-');
  return `${sanitizedSlot}_${sanitizedId}_${sanitizedTagName}`;
};

export const isRedirectToPDPEnabled = (navProp) =>
  isEmpty(navProp) || navProp?.toLowerCase() === 'outfits';

export const isCarouselEnabled = (displayType) =>
  isEmpty(displayType) || displayType?.toLowerCase() === 'carousel';

export const getLocalizedImage = (language, defaultUri, frenchUri, spanishUri) => {
  const localizedUris = {
    es: spanishUri,
    fr: frenchUri,
  };
  return !isEmpty(localizedUris[language]) ? localizedUris[language] : defaultUri;
};

export const getImageUrl = (
  desktopImageUri,
  mobileImageUri,
  frenchDesktopUri,
  frenchMobileUri,
  spanishDesktopUri,
  spanishMobileUri
) => {
  const { language } = getAPIConfig() || {};
  const { isMobile } = getViewportInfo() || {};

  return isMobile
    ? getLocalizedImage(language, mobileImageUri, frenchMobileUri, spanishMobileUri)
    : getLocalizedImage(language, desktopImageUri, frenchDesktopUri, spanishDesktopUri);
};

export const getImageUri = (tileImage, defaultImageUrl) => {
  if (isEmpty(tileImage)) return defaultImageUrl;

  const { assetHostTCP } = getAPIConfig();
  const isAbsoluteUrl = /^http/i.test(tileImage);
  return isAbsoluteUrl ? tileImage : `${assetHostTCP}/${tileImage}`;
};

export const goToOutFit = ({ id, subItemsId, icidParam }) => {
  const outfitUrl = `/outfit?outfitId=${id}&vendorColorProductIdsList=${subItemsId}&viaModule=Stylitics_Outfit_Carousel&viaPage=plp`;
  const pathVal = `/outfit/${id}/${subItemsId}`;
  const outfitPath = icidParam ? `${pathVal}?icid=${icidParam}` : pathVal;
  routerPush(outfitUrl, outfitPath);
};

export const isCombineImageViewEnabled = (displayType) => displayType?.toLowerCase() === 'combine';

export const getFullRowImageView = (displayType, maxItemCount) => {
  if (displayType?.toLowerCase() !== 'combine')
    return {
      isSingleImageRowView: false,
      isDoubleImageRowView: false,
      isThreeImageRowView: false,
      isFourImageRowView: false,
    };

  const defaultRowView = {
    isSingleImageRowView: false,
    isDoubleImageRowView: false,
    isThreeImageRowView: false,
    isFourImageRowView: false,
  };

  const rowViewMapping = {
    1: { isSingleImageRowView: true },
    2: { isDoubleImageRowView: true },
    3: { isThreeImageRowView: true },
    4: { isFourImageRowView: true },
  };

  return { ...defaultRowView, ...rowViewMapping[maxItemCount] };
};

export const getListItem = (listData, isTabletView, isMobileView) => {
  if (isMobileView) {
    return listData.slice(0, 2);
  }
  if (isTabletView) {
    return listData.slice(0, 3);
  }
  return listData;
};

export const getColumnImageUrl = (index, ...columnImages) => {
  const images = columnImages.filter(Boolean);
  return images[index];
};

export const extractAspectRatios = (aspectRatioStr) => {
  const aspectRatios = {
    aspectRatioD: null,
    aspectRatioT: null,
    aspectRatioM: null,
  };

  const ratioPairs = aspectRatioStr?.split('|');

  ratioPairs?.forEach((pair) => {
    const [key, value] = pair.split('-');
    switch (key) {
      case 'D':
        aspectRatios.aspectRatioD = parseInt(value, 10);
        break;
      case 'T':
        aspectRatios.aspectRatioT = parseInt(value, 10);
        break;
      case 'M':
        aspectRatios.aspectRatioM = parseInt(value, 10);
        break;
      default:
        console.warn(`Unknown key: ${key}`);
    }
  });

  return aspectRatios;
};

export const getListAnimation = (type) => {
  const animationMap = {
    flip: { isFlipAnimation: true },
    slide: { isSlideAnimation: true },
  };

  const selected = !isEmpty(type) && animationMap[type];

  return {
    isDefaultAnimation: !selected,
    isFlipAnimation: selected?.isFlipAnimation || false,
    isSlideAnimation: selected?.isSlideAnimation || false,
  };
};

export const isSingleImageViewEnabled = (displayType) => displayType?.toLowerCase() === 'single';

export const getCombineImageView = (isFullRowView, isDoubleTileView, isCombine) =>
  (isFullRowView || isDoubleTileView) && isCombine;

export const getItemProps = (
  displayType,
  maxItem,
  isFullRowView,
  isSingleTileView,
  isDoubleTileView
) => {
  const isSingle = isSingleImageViewEnabled(displayType);
  const isCombine = isCombineImageViewEnabled(displayType);
  const isCombineImageDesign = getCombineImageView(isFullRowView, isDoubleTileView, isCombine);

  if (isSingleTileView) {
    return {
      maxItemCount: 1,
      isScrollableCarousel: false,
      isCombineImageDesign: false,
      isSingleTileDesign: true,
      isMultiTileDesign: false,
    };
  }

  if (isDoubleTileView) {
    if (isSingle) {
      return {
        maxItemCount: 1,
        isScrollableCarousel: false,
        isCombineImageDesign: false,
        isSingleTileDesign: true,
        isMultiTileDesign: false,
      };
    }
    if (isCombineImageDesign) {
      return {
        maxItemCount: 2,
        isScrollableCarousel: false,
        isCombineImageDesign: true,
        isSingleTileDesign: true,
        isMultiTileDesign: false,
      };
    }
    return {
      maxItemCount: 2,
      isScrollableCarousel: false,
      isCombineImageDesign: false,
      isSingleTileDesign: false,
      isMultiTileDesign: true,
    };
  }

  if (isFullRowView) {
    if (isSingle) {
      return {
        maxItemCount: 1,
        isScrollableCarousel: false,
        isCombineImageDesign: false,
        isSingleTileDesign: true,
        isMultiTileDesign: false,
      };
    }

    if (isCombineImageDesign) {
      const view = getFullRowImageView(displayType, maxItem);
      const viewMap = {
        isSingleImageRowView: 1,
        isDoubleImageRowView: 2,
        isThreeImageRowView: 3,
        isFourImageRowView: 4,
      };
      const match = Object.entries(viewMap).find(([key]) => view[key]);

      if (match) {
        const [, count] = match;
        return {
          maxItemCount: count,
          isScrollableCarousel: false,
          isCombineImageDesign: true,
          isSingleTileDesign: true,
          isMultiTileDesign: false,
        };
      }
    }

    return {
      maxItemCount: maxItem,
      isScrollableCarousel: true,
      isCombineImageDesign: false,
      isSingleTileDesign: false,
      isMultiTileDesign: true,
    };
  }

  return {
    maxItemCount: maxItem,
    isScrollableCarousel: true,
    isCombineImageDesign: false,
    isSingleTileDesign: false,
    isMultiTileDesign: true,
  };
};

export const getCarouselDesign = (isScrollableCarousel, isMultiTileDesign) =>
  isScrollableCarousel && isMultiTileDesign;

export const getDoubleWidthTwoItems = (isDoubleTileView, isMultiTileDesign) =>
  isDoubleTileView && isMultiTileDesign;

export const getDoubleWidthSingleItem = (isDoubleTileView, isSingleTileDesign) =>
  isDoubleTileView && isSingleTileDesign;

export const getStyliticsLabels = (PLPLabels) => {
  const plpTileLabels = getLabelFromCopyOrFromTranslation('PLP.plpTiles', PLPLabels);
  return {
    modalHeaderText: renderLabel(
      plpTileLabels.lbl_stylitics_modal_header_text,
      'lbl_stylitics_modal_header_text'
    ),
    shopButtonText: renderLabel(
      plpTileLabels.lbl_stylitics_shop_button_text,
      'lbl_stylitics_shop_button_text'
    ),
    oosOverlayText: renderLabel(
      plpTileLabels.lbl_stylitics_oos_overlay_text,
      'lbl_stylitics_oos_overlay_text'
    ),
    backToLook: renderLabel(
      plpTileLabels.lbl_stylitics_back_to_look_text,
      'lbl_stylitics_back_to_look_text'
    ),
  };
};

export const logOutOfStockItemsOnce = ({ isLoading, dataList, uniqueKey, logger }) => {
  if (isLoading) return;

  try {
    const hasLogged = sessionStorage.getItem(`hasLogged_${uniqueKey}`);
    if (hasLogged || dataList.length === 0) return;

    const outOfStockItems = dataList[0]?.isOutOfStock
      ? [{ id: dataList[0].id, outOfStockItemsId: dataList[0].outOfStockItemsId }]
      : dataList
          .filter((item) => item.isOutOfStock)
          .map(({ id, outOfStockItemsId }) => ({ id, outOfStockItemsId }));

    if (outOfStockItems.length > 0) {
      outOfStockItems.forEach(({ id, outOfStockItemsId }) => {
        logger.info(
          `For Account ID ${id}, on the ${uniqueKey} configuration, All the products are out of stock: IDs: ${outOfStockItemsId}.`
        );
      });

      sessionStorage.setItem(`hasLogged_${uniqueKey}`, 'true');
    }
  } catch (err) {
    logger.error(`Shop by Look | Error accessing sessionStorage - `, err);
  }
};

export const getUniqueClassName = ({
  slotName,
  isFullRowView,
  isDoubleTileView,
  isSingleTileView,
  isSingleTileDesign,
  isCombineImageDesign,
}) => {
  if (isSingleTileView) {
    return `${slotName}-single-espot-variation`;
  }

  const isDoubleOrFullView = isDoubleTileView || isFullRowView;
  if (isDoubleOrFullView) {
    if (isSingleTileDesign && !isCombineImageDesign) {
      return `${slotName}-espot-variation`;
    }

    if (isCombineImageDesign) {
      return `${slotName}-combine-variation`;
    }

    return isDoubleTileView ? `${slotName}-two-column-variation` : `${slotName}-carousel-variation`;
  }

  return '';
};

export const logTileInteraction = (id, uniqueKey, outOfStockItemsId, logger) => {
  const logKey = `hasLoggedTilePress_${id}_${uniqueKey}`;
  if (!sessionStorage.getItem(logKey)) {
    logger.info(
      `For Account ID ${id}, on the ${uniqueKey} configuration, a few products are out of stock. IDs: ${outOfStockItemsId}.`
    );
    sessionStorage.setItem(logKey, 'true');
  }
};

export const trackAdobeAnalytics = (value) => {
  pushEventToAdobeAnalytics('stylitics_shoppable_image_click', value);
};

export const trackProductListImpression = (productsList, adobeAnalyticsProps) => {
  const { processedBreadCrumbs, User, APIConfig, session, uniqueKey } = adobeAnalyticsProps;
  const pageName = getPLPPageName(processedBreadCrumbs);
  const pageDataAdobe = {
    pageName,
    pageSection: processedBreadCrumbs?.[1]?.displayName,
    pageSubSection: processedBreadCrumbs?.[2]?.displayName,
    pageSubSection2: processedBreadCrumbs?.[3]?.displayName,
    pageType: 'product listing page',
  };
  const categoryId =
    processedBreadCrumbs && processedBreadCrumbs.length > 0
      ? `${processedBreadCrumbs?.[processedBreadCrumbs.length - 1]?.categoryId} | ${
          processedBreadCrumbs?.[processedBreadCrumbs.length - 1]?.displayName
        }`
      : undefined;
  const formattedProductList = productsList.map((product, index) => {
    return {
      id: product?.remoteId,
      name: product?.name,
      position: index,
      category: categoryId,
      dimension35: product?.brand,
      price: product?.discountedPrice,
      listPrice: product?.productPrice,
      shoppableImage152: uniqueKey,
    };
  });

  pushEventToAdobeAnalytics('product_impression', {
    productImpressions: formattedProductList,
    pageView: { pageData: pageDataAdobe, User, APIConfig, session },
  });
};

export const isAnimationAllowed = ({
  animationProps: { isDefaultAnimation },
  isCarouselDesign,
  isSingleTileDesign,
  isCombineImageDesign,
}) => {
  const isDoubleTile = isSingleTileDesign && !isCombineImageDesign;
  const reasonsToDisable = [isDoubleTile, isCarouselDesign, isDefaultAnimation];

  return !reasonsToDisable.some(Boolean);
};
